import { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Divider,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerCloseButton,
  DrawerHeader,
  DrawerFooter,
  Text,
  Icon,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  InputGroup,
  InputRightElement,
  Input,
  Image,
  Button,
  Spinner,
  Select,
} from '@chakra-ui/react';

import mediaQueryStateAtom from '~/recoil/atom/mediaQueryState';
import { useRecoilValue } from 'recoil';

import { IoWalletOutline, IoEyeOff, IoEye, IoAlertCircleOutline } from 'react-icons/io5';

import { InputVariant } from '~/theme/components/Input';
import { BtnVariant } from '~/theme/components/Button';

import useCustomer from './hooks/useCustomer';
import useWalletForm from './hooks/useWalletForm';

import tzWalletImage from '~/assets/images/tzWallet.png';
import { TzWalletSystem } from '~/types/common';

type LoginModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export default function TzWalletModal({ isOpen, onClose }: LoginModalProps) {
  const [displayId, setDisplayId] = useState<boolean>(false);
  const { isLargerThan768 } = useRecoilValue(mediaQueryStateAtom);

  const { maskedId, handleRemoveTzWallet } = useCustomer();

  const { walletState, handleChangeId, handleChangeSystem, isLoading } = useWalletForm();

  useEffect(() => {
    handleChangeId('');
  }, [isOpen]);

  if (isLargerThan768) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="sm" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent h="95%">
          <ModalCloseButton />
          <ModalHeader mt={6} p={0}>
            <HStack justifyContent="center" borderBottom="1px" borderColor="primary" py={2}>
              <Icon as={IoWalletOutline} boxSize="1.5rem" color="primary" />
              <Text fontSize="lg" fontWeight="normal">
                慈濟錢包帳號綁定
              </Text>
            </HStack>
          </ModalHeader>

          <ModalBody>
            {isLoading ? (
              <VStack spacing={4}>
                <Text fontSize="md" fontWeight="bold" textAlign="center">
                  確認中，請稍候...
                </Text>
                <Spinner size="xl" />
              </VStack>
            ) : (
              <VStack align="stretch" spacing={4}>
                {maskedId ? (
                  <>
                    <Text fontSize="md" fontWeight="bold" textAlign="center">
                      您所綁定的慈濟錢包
                    </Text>
                    <VStack backgroundColor="gray.50" spacing={4} py={4}>
                      <Text fontSize="sm" textAlign="center">
                        系統僅記錄身份證後三碼
                      </Text>
                      <Text fontSize="sm" textAlign="center">
                        {maskedId}
                      </Text>
                      <Button variant={BtnVariant.OUTLINE} onClick={handleRemoveTzWallet} size="lg">
                        移除綁定
                      </Button>
                    </VStack>
                  </>
                ) : (
                  <>
                    <Text fontSize="md" fontWeight="bold" textAlign="center">
                      歡迎使用慈濟錢包
                    </Text>
                    <Image src={tzWalletImage} />
                    <FormControl isRequired isInvalid={!!walletState.errors.system}>
                      <FormLabel cursor="pointer" fontSize="sm" fontWeight="normal">
                        服務單位
                      </FormLabel>
                      <Select
                        cursor="pointer"
                        value={walletState.state.system}
                        onChange={(e) => handleChangeSystem(e.target.value as TzWalletSystem)}
                      >
                        <option value={TzWalletSystem.HOSPITAL}>
                          醫療法人、醫院、基金會、大愛台
                        </option>
                        <option value={TzWalletSystem.TCUST}>慈濟科大</option>
                      </Select>
                      <FormErrorMessage>{walletState.errors.system}</FormErrorMessage>
                    </FormControl>

                    <FormControl isRequired isInvalid={!!walletState.errors.id}>
                      <FormLabel cursor="pointer" fontSize="md" fontWeight="normal">
                        身份證字號/居留證號碼(第一個字母需大寫)
                      </FormLabel>
                      <InputGroup>
                        <Input
                          variant={InputVariant.OUTLINE_DARK}
                          value={walletState.state.id}
                          onChange={(e) => handleChangeId(e.target.value)}
                          type={displayId ? 'text' : 'password'}
                          inputMode="text"
                          autoComplete="new-password"
                        />
                        <InputRightElement width="4.5rem">
                          <Icon
                            as={displayId ? IoEye : IoEyeOff}
                            boxSize="1.5rem"
                            color="dark"
                            onClick={() => setDisplayId((prev) => !prev)}
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage bgColor="danger.200" p={2}>
                        <Icon
                          as={IoAlertCircleOutline}
                          color="danger.600"
                          boxSize="1.25rem"
                          mr={2}
                        />
                        {walletState.errors.id}
                      </FormErrorMessage>
                    </FormControl>
                  </>
                )}
                <Text fontSize="xs">
                  系統不會儲存您的個人資料，身份證號僅供慈濟錢包驗證。驗證後系統將自動將您的身份證號資料刪除，僅保留後3碼作為使用者的確認。
                </Text>
                <Divider />
                <VStack align="stretch" spacing={0}>
                  <Text fontSize="sm">提醒您</Text>
                  <Text fontSize="xs">
                    系統不提供慈濟錢包退款服務，如需退款服務需與平台管理人員聯繫。
                  </Text>
                </VStack>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter p={0}>
            <Button bgColor="primary" borderRadius="0" width="100%" onClick={onClose}>
              確認
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="full">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader my={6} p={0}>
          <HStack justifyContent="center" borderBottom="1px" borderColor="primary" py={2}>
            <Icon as={IoWalletOutline} boxSize="1.5rem" color="primary" />
            <Text fontSize="lg" fontWeight="normal">
              慈濟錢包帳號綁定
            </Text>
          </HStack>
        </DrawerHeader>

        <DrawerBody>
          {isLoading ? (
            <VStack spacing={4}>
              <Text fontSize="md" fontWeight="bold" textAlign="center">
                確認中，請稍候...
              </Text>
              <Spinner size="xl" />
            </VStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              {maskedId ? (
                <>
                  <Text fontSize="md" fontWeight="bold" textAlign="center">
                    您所綁定的慈濟錢包
                  </Text>
                  <VStack backgroundColor="gray.50" spacing={4} py={4}>
                    <Text fontSize="sm" textAlign="center">
                      系統僅記錄身份證後三碼
                    </Text>
                    <Text fontSize="sm" textAlign="center">
                      {maskedId}
                    </Text>
                    <Button variant={BtnVariant.OUTLINE} onClick={handleRemoveTzWallet} size="lg">
                      移除綁定
                    </Button>
                  </VStack>
                </>
              ) : (
                <>
                  <Text fontSize="md" fontWeight="bold" textAlign="center">
                    歡迎使用慈濟錢包
                  </Text>
                  <Image src={tzWalletImage} />

                  <FormControl isRequired isInvalid={!!walletState.errors.system}>
                    <FormLabel fontSize="sm" fontWeight="normal">
                      服務單位
                    </FormLabel>
                    <Select
                      value={walletState.state.system}
                      onChange={(e) => handleChangeSystem(e.target.value as TzWalletSystem)}
                    >
                      <option value={TzWalletSystem.HOSPITAL}>
                        醫療法人、醫院、基金會、大愛台
                      </option>
                      <option value={TzWalletSystem.TCUST}>慈濟科大</option>
                    </Select>
                    <FormErrorMessage>{walletState.errors.system}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={!!walletState.errors.id}>
                    <FormLabel fontSize="md" fontWeight="normal">
                      身份證字號/居留證號碼
                    </FormLabel>
                    <InputGroup>
                      <Input
                        variant={InputVariant.OUTLINE_DARK}
                        value={walletState.state.id}
                        onChange={(e) => handleChangeId(e.target.value)}
                        type={displayId ? 'text' : 'password'}
                        inputMode="text"
                        autoComplete="new-password"
                      />
                      <InputRightElement width="4.5rem">
                        <Icon
                          as={displayId ? IoEye : IoEyeOff}
                          boxSize="1.5rem"
                          color="dark"
                          onClick={() => setDisplayId((prev) => !prev)}
                        />
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage bgColor="danger.200" p={2}>
                      <Icon as={IoAlertCircleOutline} color="danger.600" boxSize="1.25rem" mr={2} />
                      {walletState.errors.id}
                    </FormErrorMessage>
                  </FormControl>
                </>
              )}
              <Text fontSize="xs">
                系統不會儲存您的個人資料，身份證號僅供慈濟錢包驗證。驗證後系統將自動將您的身份證號資料刪除，僅保留後3碼作為使用者的確認。
              </Text>
              <Divider />
              <VStack align="stretch" spacing={0}>
                <Text fontSize="sm">提醒您</Text>
                <Text fontSize="xs">
                  系統不提供慈濟錢包退款服務，如需退款服務需與平台管理人員聯繫。
                </Text>
              </VStack>
            </VStack>
          )}
        </DrawerBody>
        <DrawerFooter p={0}>
          <Button bgColor="primary" borderRadius="0" width="100%" onClick={onClose}>
            確認
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
